import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import { optimizerLink } from '../../lib/ui-links.js';

interface OptimizeCancelOptions {
  force?: boolean;
}

/**
 * Register the `optimize cancel` subcommand.
 * Extracted to optimize-cancel.ts to keep optimize.ts under the 500-line limit.
 */
export function registerOptimizeCancelCommand(optimize: Command, prompts: Command): void {
  optimize
    .command('cancel')
    .description('Cancel a running optimization job')
    .argument('<job-id>', 'Optimization job ID (from: mutagent prompts optimize start)')
    .option('--force', 'Skip confirmation prompt')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts optimize cancel <job-id> --force
  ${chalk.dim('$')} mutagent prompts optimize cancel <job-id> --json
  ${chalk.dim('$')} mutagent prompts optimize cancel <job-id> --force --json

${chalk.yellow('Cost warning:')} Cancelling a running job may still incur LLM cost for the current iteration.
${chalk.dim('Jobs with status "completed" or "cancelled" cannot be cancelled again.')}
    `)
    .action(async (jobId: string, options: OptimizeCancelOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Pre-check: fetch current status
        const client = await getSDKClient();
        const jobStatus = await client.getOptimizationStatus(jobId);
        const status = typeof jobStatus.status === 'string' ? jobStatus.status : 'unknown';

        if (status === 'completed' || status === 'cancelled') {
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Job ${jobId} has status "${status}" and cannot be cancelled`,
            `View results: mutagent prompts optimize results ${jobId}\n` +
            `Dashboard: ${optimizerLink(jobId, jobId)}`
          );
        }

        // Require --force or --json for confirmation
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Cancelling optimization job ${jobId} requires confirmation`,
            'Run: mutagent prompts optimize cancel --help\n' +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent prompts optimize cancel ${jobId} --force\n` +
            `Note: Cancellation may still incur LLM cost for the current iteration.`
          );
        }

        const result = await client.cancelOptimization(jobId);

        if (isJson) {
          output.output({
            success: result.success,
            jobId,
            _links: {
              optimizer: optimizerLink(jobId, jobId),
            },
          });
        } else {
          output.success(`Optimization job ${jobId} cancelled successfully`);
          output.info(`View status: mutagent prompts optimize status ${jobId}`);
          output.info(`Dashboard: ${optimizerLink(jobId, jobId)}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
